import { useEffect } from 'react';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import { getSSRData } from './getSSRData';
import type { SiteUserTraits } from './preloadSiteUserTraits';

const SSR_SITE_USER_TRAITS_PROPERTY = '__SSR_SITE_USER_TRAITS__';

export type SiteUserTraitsState = {
	data: SiteUserTraits | null;
	loading: boolean;
	error: Error | null;
};

// Initialize with SSR data if available (runs at module load time, before React renders)
const getInitialState = (): SiteUserTraitsState => {
	const ssrData = getSSRData<SiteUserTraits>(SSR_SITE_USER_TRAITS_PROPERTY);

	return {
		data: ssrData,
		loading: false,
		error: null,
	};
};

const initialState: SiteUserTraitsState = getInitialState();

const actions = {
	start:
		() =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState({ loading: true, error: null });
		},
	success:
		(data: SiteUserTraits) =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState({ data, loading: false, error: null });
		},
	error:
		(err: Error) =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState({ error: err, loading: false });
		},
	reset:
		() =>
		({ setState }: { setState: (s: Partial<SiteUserTraitsState>) => void }) => {
			setState(getInitialState());
		},
};

export const siteUserTraitsStore = createStore({
	initialState,
	actions,
	name: 'siteUserTraitsStore',
});

export const useSiteUserTraitsStore = createHook(siteUserTraitsStore);
export const useSiteUserTraitsActions = createActionsHook(siteUserTraitsStore);

async function fetchSiteUserTraitsData(cloudId: string, userId: string): Promise<SiteUserTraits> {
	const res = await cfetch(
		`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${userId}`,
		{ method: 'GET' },
	);
	if (!res.ok) {
		const text = await res.text().catch(() => '');
		throw new Error(`Request failed with ${res.status}: ${text || res.statusText}`);
	}
	return (await res.json()) as SiteUserTraits;
}

// Use a module-level variable to ensure we only fetch once per app lifecycle
// This way multiple components using the hook won't trigger multiple fetches
let hasFetched: boolean = false;

// Test utility to reset the hasFetched flag
export function resetHasFetched() {
	hasFetched = false;
}

export function useSiteUserTraits({ skipBootstrap }: { skipBootstrap?: boolean } = {}): {
	data: SiteUserTraits | null;
	loading: boolean;
	error: Error | null;
} {
	const [{ data, loading, error }, storeActions] = useSiteUserTraitsStore();
	const { accessStatus, cloudId, userId } = useSessionData();
	const hasLicensedAccess =
		accessStatus === 'LICENSED_USE_ACCESS' || accessStatus === 'LICENSED_ADMIN_ACCESS';

	useEffect(() => {
		if (data || loading || error) return;

		if (hasFetched || skipBootstrap) return;
		if (!hasLicensedAccess || !cloudId || !userId) return;

		hasFetched = true;
		storeActions.start();
		fetchSiteUserTraitsData(cloudId, userId).then(storeActions.success).catch(storeActions.error);
	}, [skipBootstrap, data, loading, error, storeActions, hasLicensedAccess, cloudId, userId]);

	return { data, loading, error };
}
